import { EmptyState } from "@/components/common/EmptyState";
import { SeatStatusBadge } from "@/components/common/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { RadarChart } from "@/components/progress/RadarChart";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrgProgress } from "@/hooks";
import { companyProjectionSpokes, companySpokes } from "@/lib/radar";
import type { MemberProgress } from "@/types/api";
import { Link } from "react-router-dom";

/**
 * Where the organisation is learning, and who is learning what.
 *
 * ACTIVITY, not mastery: the radar says which domains someone has worked
 * through, never how well they know them. There are deliberately no scores on
 * this screen — see RadarChart for why.
 *
 * Every load is audited server-side as `member.viewed`, like the People screen.
 */
export function AnalyticsPage() {
	const { data, isLoading } = useOrgProgress();

	const company = data?.company ?? null;
	const projection = company?.projection ?? null;
	const members = data?.members ?? [];
	const started = members.filter((m) => (m.radar?.totals.credit ?? 0) > 0);
	const hasCompanyRadar = Boolean(
		company && (company.axes.length > 0 || (projection?.axes.length ?? 0) > 0),
	);

	return (
		<>
			<PageHeader
				title="Analytics"
				description="What your team is learning, and how much of it they are getting through."
			/>

			{isLoading ? (
				<Skeleton className="mb-6 h-80 w-full" />
			) : !company || !hasCompanyRadar ? (
				<EmptyState
					title="No learning yet"
					description="Once people start reading, listening or taking a whiteboard lesson, their domains appear here."
				/>
			) : (
				<>
					<Card className="mb-6">
						<CardContent className="flex flex-col gap-6 p-5 md:flex-row md:items-center">
							<div className="md:w-[340px] md:shrink-0">
								<RadarChart
									spokes={
										projection?.axes.length
											? companyProjectionSpokes(company)
											: companySpokes(company)
									}
									rings={company.rings}
									showBefore={false}
									title={`Where ${company.activeMembers} of ${company.members} people are learning`}
								/>
								{(projection?.additional.assigned ?? 0) > 0 ? (
									<p className="mt-1 text-center text-caption text-muted-foreground">
										Dashed orange: if all planned tasks had been completed
									</p>
								) : null}
							</div>

							<div className="min-w-0 flex-1">
								<h2 className="mb-1 text-body font-semibold text-heading">
									Across the company
								</h2>
								<p className="mb-4 max-w-prose text-caption text-muted-foreground">
									Each point is a domain someone has worked in. The solid shape
									shows completed activity
									{(projection?.additional.assigned ?? 0) > 0
										? `; the dashed shape shows the progress offered by ${projection?.tasks.unfinished ?? 0} planned tasks that were not completed.`
										: "."}
								</p>

								<div className="mb-4 flex flex-wrap items-baseline gap-x-8 gap-y-3">
									<Stat
										label="Learning"
										value={`${company.activeMembers} of ${company.members}`}
									/>
									<Stat
										label="Pieces worked through"
										value={company.axes.reduce((n, a) => n + a.pieces, 0)}
									/>
									<Stat label="Domains" value={company.axes.length} />
								</div>

								<ul className="flex flex-col gap-1.5">
									{company.axes.slice(0, 6).map((axis) => (
										<li
											key={axis.key}
											className="flex items-baseline gap-3 text-body"
										>
											<span className="min-w-0 flex-1 truncate text-heading">
												{axis.domain}
											</span>
											<span className="tabular shrink-0 text-caption text-muted-foreground">
												{axis.members}{" "}
												{axis.members === 1 ? "person" : "people"}
											</span>
										</li>
									))}
								</ul>
							</div>
						</CardContent>
					</Card>

					<Card>
						<div className="overflow-x-auto">
							<table className="w-full text-body">
								<thead>
									<tr className="border-b border-divider text-caption uppercase tracking-wide text-muted-foreground">
										<th className="px-5 py-2.5 text-left font-medium">
											Person
										</th>
										<th className="px-5 py-2.5 text-left font-medium">
											Status
										</th>
										<th className="px-5 py-2.5 text-left font-medium">
											Learning in
										</th>
										<th className="px-5 py-2.5 text-right font-medium">
											Pieces
										</th>
									</tr>
								</thead>
								<tbody>
									{members.map((member) => (
										<MemberRow key={member.memberId} member={member} />
									))}
								</tbody>
							</table>
						</div>
					</Card>

					<p className="mt-3 text-caption text-muted-foreground">
						{started.length} of {members.length} people have started. Opening
						someone's analytics is recorded in your activity log.
					</p>
				</>
			)}
		</>
	);
}

function MemberRow({ member }: { member: MemberProgress }) {
	const axes = member.radar?.axes ?? [];
	const top = [...axes].sort((a, b) => b.points.now - a.points.now).slice(0, 3);
	const started = (member.radar?.totals.credit ?? 0) > 0;
	const name = member.name ?? member.email;

	return (
		<tr className="border-b border-divider last:border-0 hover:bg-surface2">
			<td className="px-5 py-3">
				{/* A whole page, not an expanding row: this is where an admin comes
				    to understand one person, and that deserves its own address. */}
				<Link
					to={`/analytics/${member.memberId}`}
					className="font-medium text-heading underline-offset-4 hover:underline focus-visible:underline focus-visible:outline-none"
				>
					{name}
				</Link>
				{member.name ? (
					<div className="text-caption text-muted-foreground">
						{member.email}
					</div>
				) : null}
			</td>
			<td className="px-5 py-3">
				<SeatStatusBadge status={member.seatStatus} />
			</td>
			<td className="px-5 py-3">
				{started ? (
					<div className="flex flex-wrap gap-1.5">
						{top.map((axis) => (
							<span
								key={axis.key}
								className="inline-flex items-center rounded-full border border-divider bg-surface3 px-2.5 py-1 text-caption text-paragraph"
							>
								{axis.domain}
							</span>
						))}
					</div>
				) : (
					<span className="text-caption text-muted-foreground">
						{member.radar ? "Has not started" : "Seat not claimed"}
					</span>
				)}
			</td>
			<td className="tabular px-5 py-3 text-right text-paragraph">
				{member.radar?.pieces ?? 0}
			</td>
		</tr>
	);
}

function Stat({ label, value }: { label: string; value: string | number }) {
	return (
		<div>
			<div className="tabular text-h3 font-semibold text-heading">{value}</div>
			<div className="text-caption text-muted-foreground">{label}</div>
		</div>
	);
}
